// TeamLeadSignup.jsx
import React, { useState, useMemo } from 'react';
import { useInventory } from '../context/InventoryContext';
import { useAuth } from '../context/AuthContext';
import { FaTrash, FaPlus, FaEdit } from 'react-icons/fa';
import Pagination from '../components/Pagination';

// Fallback in-memory storage array to fix the undefined runtime references
const staticTeamLeads = [];

const TeamLeadSignup = () => {
  // Invoked to satisfy background context setups if needed
  useInventory();
  const { users = [], addUser, deleteUser, updateUser } = useAuth(); 
  
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false); 
  const [editingUserId, setEditingUserId] = useState(null); 

  const [form, setForm] = useState({
    username: '', password: ''
  });
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ message: '', type: '', visible: false });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchQuery, setSearchQuery] = useState('');

  // Notification functions
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type, visible: true });
    setTimeout(() => {
      setNotification({ message: '', type: '', visible: false });
    }, 3000);
  };

  // 1. Get only teamlead users combined with static data local fallbacks
  const teamleads = useMemo(() => {
    const contextTeamLeads = users.filter(user => user.role === "teamlead");
    // Merge context users and our defined static local array securely
    return [...contextTeamLeads, ...staticTeamLeads];
  }, [users]);

  // 2. Apply search filter
  const filteredTeamLeads = useMemo(() => {
    return teamleads.filter(teamlead => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (
        teamlead.username.toLowerCase().includes(q) ||
        (teamlead.id || '').toLowerCase().includes(q)
      );
    });
  }, [teamleads, searchQuery]);

  // 3. Paginate
  const totalItems = filteredTeamLeads.length;
  const paginatedTeamLeads = useMemo(() => {
    return filteredTeamLeads.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize
    );
  }, [filteredTeamLeads, currentPage, pageSize]);

  const resetForm = () => {
    setForm({ username: '', password: '' });
    setError('');
    setIsEditing(false);
    setEditingUserId(null);
  };

  const handleEditClick = (teamlead) => {
    setError('');
    setIsEditing(true);
    setEditingUserId(teamlead.id);
    setForm({
      username: teamlead.username,
      password: '' // Explicitly empty so it is clear it isn't mandatory
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (isEditing) {
      if (!form.username.trim()) {
        setError('Username is required.');
        return;
      }
    } else {
      if (!form.username.trim() || !form.password) {
        setError('Username and Password are required.');
        return;
      }
    }

    // Check if username exists (including static data)
    const usernameExists = teamleads.some(u => 
      u.username === form.username && u.id !== editingUserId
    );
    if (usernameExists) {
      setError('Username already exists.');
      return;
    }

    if (isEditing) {
      const updatedData = {
        username: form.username.trim(),
        role: 'teamlead'
      };
      
      // Password field is completely optional—appended only if typed out
      if (form.password && form.password.trim() !== '') {
        updatedData.password = form.password;
      }

      if (updateUser) {
        updateUser(editingUserId, updatedData);
        showNotification(`TeamLead "${form.username.trim()}" has been updated successfully`, 'success');
      } else {
        // Find inside static fallback storage array if auth provider context fails
        const index = staticTeamLeads.findIndex(s => s.id === editingUserId);
        if (index !== -1) {
          staticTeamLeads[index] = { ...staticTeamLeads[index], ...updatedData };
        }
        showNotification(`TeamLead "${form.username.trim()}" updated successfully`, 'success');
      }
    } else {
      // Check if trying to add to static data (prevent duplicates)
      const isStatic = staticTeamLeads.some(s => s.username === form.username.trim());
      if (isStatic) {
        setError('This username exists in static data. Please choose a different username.');
        return;
      }

      if (addUser) {
        addUser({
          username: form.username.trim(),
          password: form.password,
          role: 'teamlead'
        });
        showNotification(`TeamLead "${form.username.trim()}" has been added successfully`, 'success');
      } else {
        // Fallback: add to static data array
        const newUser = {
          id: `tl-static-${Date.now()}`,
          username: form.username.trim(),
          password: form.password,
          role: 'teamlead'
        };
        staticTeamLeads.push(newUser);
        showNotification(`TeamLead "${form.username.trim()}" has been added successfully`, 'success');
      }
    }

    resetForm();
    setShowForm(false);
  };

  const handleDelete = (id) => {
    const teamlead = teamleads.find(user => user.id === id);
    if (!teamlead) return;

    if (window.confirm(`Delete "${teamlead.username}" ?`)) {
      if (deleteUser) {
        deleteUser(id, "teamlead");
      } else {
        // Fallback array handling deletion mapping support
        const index = staticTeamLeads.findIndex(s => s.id === id);
        if (index !== -1) staticTeamLeads.splice(index, 1);
      }
      showNotification(`Team Lead "${teamlead.username}" deleted`, "error");
    }
  };

  return (
    <div>
      {/* Notification */}
      {notification.visible && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-3 rounded-xl shadow-lg transition-all duration-300 ${
          notification.type === 'error' ? 'bg-red-500' : 'bg-green-500'
        } text-white font-medium`}>
          {notification.message}
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Team Lead Users</h1>
        <div className="flex gap-3">
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary">
            <FaPlus /> Create Team Lead
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by username or ID…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full max-w-md rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        {/* Table scroll logic */}
        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedTeamLeads.length === 0 ? (
                <tr>
                  <td colSpan="3" className="px-4 py-8 text-center text-sm text-gray-500">
                    No team leads created yet.
                  </td>
                </tr>
              ) : (
                paginatedTeamLeads.map(teamlead => {
                  // Safely determine if this item belongs to local fallback static array elements
                  const rowIsStatic = teamlead.id && teamlead.id.toString().includes('tl-static-');
                
                  return (
                    <tr key={teamlead.id} className={`hover:bg-indigo-50/50 transition-colors ${rowIsStatic ? 'bg-blue-50/30' : ''}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {teamlead.username}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                          Team Lead
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm flex items-center space-x-3">
                        <button 
                          onClick={() => handleEditClick(teamlead)} 
                          className="text-indigo-600 hover:text-indigo-900 transition"
                          title="Edit TeamLead"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        
                        <button 
                          onClick={() => handleDelete(teamlead.id)} 
                          className="text-red-500 hover:text-red-700 transition"
                          title="Delete TeamLead"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      {/* Modal Form Dialog */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-md mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {isEditing ? "Update Team Lead" : "Create Team Lead"}
            </h2>
            {error && <div className="text-red-600 text-sm mb-4">{error}</div>}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.username}
                  onChange={e => setForm({...form, username: e.target.value})}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isEditing ? "New Password (Leave blank to keep unchanged)" : "Password"}
                </label>
                <input
                  type="password"
                  placeholder={isEditing ? "••••••••" : ""}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  required={!isEditing} 
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">
                  {isEditing ? "Save Changes" : "Create TeamLead"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamLeadSignup;